import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LAUNCH } from '../lib/launch';
import { ComingSoonBanner } from './ComingSoon';
import { PurchaseNavButton } from './CheckoutFlow';
import { API_BASE as BACKEND } from '../lib/apiBase';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${BACKEND}${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw Object.assign(new Error(data.detail?.message || data.detail || res.statusText), { status: res.status });
  }
  return res.json();
};

const EB_PATHWAYS = [
  { id: 'eb2-niw',       name: 'EB-2 National Interest Waiver', icon: 'fa-graduation-cap', category: 'EB-2' },
  { id: 'eb1a',          name: 'EB-1A Extraordinary Ability',   icon: 'fa-trophy',          category: 'EB-1' },
  { id: 'perm-employer', name: 'PERM / Employer-Sponsored',     icon: 'fa-briefcase',       category: 'EB-3' },
  { id: 'o1-nonimmigrant', name: 'O-1 Extraordinary Ability',  icon: 'fa-star',            category: 'EB-1' },
];

// Simple bar chart rendered with divs — no external library needed
const TrendChart = ({ history }) => {
  if (!history || history.length === 0) return <p className="text-primary/40 text-sm text-center py-4">No historical data available.</p>;

  const values = history.map(h => new Date(h.cutoff_date || h.bulletin_date || h.date).getTime()).filter(v => !isNaN(v));
  if (values.length === 0) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  return (
    <div className="mt-2">
      <div className="flex items-end gap-1 h-24">
        {history.slice(-12).map((h, i) => {
          const val = new Date(h.cutoff_date || h.bulletin_date || h.date).getTime();
          const pct = isNaN(val) ? 0 : ((val - min) / range) * 100;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div
                className="w-full bg-accent rounded-t-sm transition-all"
                style={{ height: `${Math.max(4, pct)}%` }}
                title={h.cutoff_date || h.bulletin_date || h.date}
              ></div>
              <span className="text-primary/40 text-xs rotate-45 origin-left truncate w-8 hidden sm:block">
                {(h.bulletin_month || '').slice(0, 3)}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-primary/50 text-xs mt-3 text-center">12-month priority date trend (higher = more current)</p>
    </div>
  );
};

const PathwayCard = ({ pathway, data, history, onSwitchClick }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [personalDate, setPersonalDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const savePersonalDate = async () => {
    if (!personalDate) return;
    setSaving(true);
    try {
      await apiFetch(`/api/priority-date/${pathway.id}/priority-date`, {
        method: 'POST',
        body: JSON.stringify({ priority_date: personalDate }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert('Failed to save: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-secondary rounded-xl shadow p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0">
          <i className={`fas ${pathway.icon} text-accent`}></i>
        </div>
        <div>
          <h3 className="font-bold text-primary font-cormorant">{pathway.name}</h3>
          <span className="text-xs text-primary/50">Visa Bulletin Category: {pathway.category}</span>
        </div>
      </div>

      {/* Stats */}
      {data ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
          <div className="bg-primary/5 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-accent">{data.current_cutoff || '—'}</div>
            <div className="text-primary/60 text-xs mt-0.5">Current Cutoff</div>
          </div>
          <div className="bg-primary/5 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-accent">
              {data.estimated_wait_months != null ? `${data.estimated_wait_months}mo` : '—'}
            </div>
            <div className="text-primary/60 text-xs mt-0.5">Est. Wait</div>
          </div>
          <div className="bg-primary/5 rounded-lg p-3 text-center col-span-2 sm:col-span-1">
            <div className="text-lg font-bold text-accent">
              {data.projected_availability_year || data.projected_availability || '—'}
            </div>
            <div className="text-primary/60 text-xs mt-0.5">Projected Availability</div>
          </div>
        </div>
      ) : (
        <div className="bg-primary/5 rounded-lg p-4 mb-5 text-center text-primary/40 text-sm">
          Priority date data not available. Complete your pathway workflow to unlock.
        </div>
      )}

      {/* Personal priority date */}
      <div className="mb-4">
        <label className="block text-primary/70 text-xs font-semibold mb-1.5">Your Personal Priority Date</label>
        <div className="flex gap-2">
          <input
            type="date"
            value={personalDate}
            onChange={e => setPersonalDate(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-accent focus:border-accent"
            placeholder="YYYY-MM-DD"
          />
          <button
            onClick={savePersonalDate}
            disabled={saving || !personalDate}
            className="px-3 py-2 bg-accent text-primary rounded-lg text-sm font-semibold hover:bg-accent/90 transition-colors disabled:opacity-50"
          >
            {saving ? <i className="fas fa-spinner fa-spin"></i> : saved ? <i className="fas fa-check text-green-700"></i> : 'Save'}
          </button>
        </div>
      </div>

      {/* History toggle */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="text-accent hover:text-accent/80 text-xs font-semibold"
        >
          <i className={`fas fa-chevron-${showHistory ? 'up' : 'down'} mr-1`}></i>
          {showHistory ? 'Hide' : 'Show'} 12-Month Trend
        </button>
        {onSwitchClick && (
          <button
            onClick={onSwitchClick}
            className="text-xs bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-semibold hover:bg-yellow-200 transition-colors"
          >
            <i className="fas fa-exchange-alt mr-1"></i>View Switch Options
          </button>
        )}
      </div>

      {showHistory && <TrendChart history={history || []} />}
    </div>
  );
};

const PriorityDatesPage = () => {
  const navigate = useNavigate();
  const [entitledPathways, setEntitledPathways] = useState([]);
  const [dashboardData, setDashboardData] = useState({});
  const [historyData, setHistoryData] = useState({});
  const [switchRecs, setSwitchRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingRecs, setGeneratingRecs] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const ent = await apiFetch('/api/me/entitlement');
        const active = ent?.active_pathways || [];
        const ebActive = EB_PATHWAYS.filter(p => active.includes(p.id));
        setEntitledPathways(ebActive);

        // Fetch dashboard + history for each EB pathway
        const dashPromises = ebActive.map(p =>
          apiFetch(`/api/priority-date/${p.id}`)
            .then(d => ({ id: p.id, data: d }))
            .catch(() => ({ id: p.id, data: null }))
        );
        const histPromises = ebActive.map(p =>
          apiFetch(`/api/priority-date/${p.id}/history`)
            .then(d => ({ id: p.id, data: d }))
            .catch(() => ({ id: p.id, data: [] }))
        );

        const [dashResults, histResults] = await Promise.all([
          Promise.all(dashPromises),
          Promise.all(histPromises),
        ]);

        const dashMap = {};
        dashResults.forEach(r => { dashMap[r.id] = r.data; });
        setDashboardData(dashMap);

        const histMap = {};
        histResults.forEach(r => { histMap[r.id] = r.data; });
        setHistoryData(histMap);

        // Switch recommendations
        const recs = await apiFetch('/api/priority-date/switch-recommendations').catch(() => []);
        setSwitchRecs(Array.isArray(recs) ? recs : []);
      } catch (e) {
        console.error('Priority dates load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const generateRecs = async () => {
    setGeneratingRecs(true);
    try {
      await apiFetch('/api/priority-date/switch-recommendations/generate', { method: 'POST' });
      const recs = await apiFetch('/api/priority-date/switch-recommendations');
      setSwitchRecs(Array.isArray(recs) ? recs : []);
    } catch (e) {
      alert('Failed to generate recommendations: ' + e.message);
    } finally {
      setGeneratingRecs(false);
    }
  };

  const dismissRec = async (id) => {
    try {
      await apiFetch(`/api/priority-date/switch-recommendations/${id}/dismiss`, { method: 'POST' });
      setSwitchRecs(prev => prev.filter(r => r.id !== id));
    } catch {/* best-effort */}
  };

  const handleSwitchCheckout = async (rec) => {
    try {
      const res = await apiFetch('/api/priority-date/switch-checkout', {
        method: 'POST',
        body: JSON.stringify({ recommendation_id: rec.id, destination_pathway_id: rec.destination_pathway_id }),
      });
      if (res.url) window.location.href = res.url;
    } catch (e) {
      alert('Failed to start checkout: ' + e.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="text-accent text-xl"><i className="fas fa-spinner fa-spin mr-3"></i>Loading priority dates…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary">
      {/* Nav */}
      <nav className="bg-primary border-b border-highlight">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
              <i className="fas fa-balance-scale text-primary text-sm"></i>
            </div>
            <span className="text-secondary font-bold text-xl font-cormorant">ImmigrateSmart</span>
          </button>
          <div className="flex items-center gap-3">
            <PurchaseNavButton />
            <button onClick={() => navigate('/dashboard')} className="text-secondary hover:text-accent transition-colors text-sm">
              <i className="fas fa-arrow-left mr-2"></i>Dashboard
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary font-cormorant mb-2">Priority Date Intelligence</h1>
          <p className="text-secondary/60">Track your priority date movement and get proactive pathway recommendations.</p>
          <div className="mt-4">
            <ComingSoonBanner dark>
              Priority date tracking and pathway switching are visible in beta. Switch purchases are not open on this launch.
            </ComingSoonBanner>
          </div>
        </div>

        {entitledPathways.length === 0 ? (
          <div className="bg-secondary rounded-xl p-8 text-center">
            <i className="fas fa-calendar-times text-accent text-4xl mb-4"></i>
            <h3 className="text-xl font-bold text-primary mb-2">No EB Pathway Active</h3>
            <p className="text-primary/60 mb-5">Priority date intelligence is available for EB-1, EB-2, and EB-3 pathways.</p>
            <div className="flex gap-3 justify-center flex-wrap">
              {EB_PATHWAYS.map(p => (
                <a key={p.id} href="/pricing" className="bg-accent text-primary px-4 py-2 rounded-lg text-sm font-semibold hover:bg-accent/90 transition-colors">
                  Get {p.name}
                </a>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Switch Recommendations */}
            {switchRecs.length > 0 && (
              <div className="mb-8 space-y-4">
                <h2 className="text-xl font-bold text-secondary font-cormorant flex items-center gap-2">
                  <i className="fas fa-lightbulb text-yellow-500"></i>
                  Pathway Switch Recommendations
                </h2>
                {switchRecs.map(rec => (
                  <div key={rec.id} className="bg-yellow-50 border border-yellow-300 rounded-xl p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="font-semibold text-yellow-900 mb-1">
                          Switch from <strong>{EB_PATHWAYS.find(p => p.id === rec.source_pathway_id)?.name || rec.source_pathway_id}</strong>
                          {' → '}
                          <strong>{EB_PATHWAYS.find(p => p.id === rec.destination_pathway_id)?.name || rec.destination_pathway_id}</strong>
                        </div>
                        <p className="text-yellow-800 text-sm">{rec.recommendation_reason}</p>
                        {rec.time_savings_months && (
                          <p className="text-yellow-700 text-xs mt-1">
                            <i className="fas fa-clock mr-1"></i>
                            Estimated {rec.time_savings_months} months faster
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleSwitchCheckout(rec)}
                          disabled={!LAUNCH.pathwaySwitchPurchases}
                          className="bg-yellow-500 text-black px-4 py-2 rounded-lg text-sm font-semibold hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {LAUNCH.pathwaySwitchPurchases ? 'Switch at 50% off' : 'Switch — coming soon'}
                        </button>
                        <button
                          onClick={() => dismissRec(rec.id)}
                          className="text-yellow-600 hover:text-yellow-800 text-xs text-center"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Generate recommendations button */}
            {switchRecs.length === 0 && (
              <div className="mb-6 text-right">
                <button
                  onClick={generateRecs}
                  disabled={generatingRecs}
                  className="bg-secondary text-primary border border-highlight px-4 py-2 rounded-lg text-sm font-semibold hover:bg-secondary/80 transition-colors disabled:opacity-50"
                >
                  <i className={`fas ${generatingRecs ? 'fa-spinner fa-spin' : 'fa-magic'} mr-2`}></i>
                  {generatingRecs ? 'Analyzing…' : 'Generate Switch Recommendations'}
                </button>
              </div>
            )}

            {/* Pathway cards */}
            <div className="space-y-6">
              {entitledPathways.map(pathway => (
                <PathwayCard
                  key={pathway.id}
                  pathway={pathway}
                  data={dashboardData[pathway.id]}
                  history={historyData[pathway.id]}
                  onSwitchClick={switchRecs.length > 0 ? undefined : generateRecs}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PriorityDatesPage;
